import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { API_BASE } from '../config';
import { PanelContainer, Title, Subtitle, PrimaryButton } from '../ui/common';
import { useTranslation } from '../translations';

// P3.2: strona powrotu po płatności kartą przy ladzie („Zapłać kartą na miejscu”). Odpytuje
// GET /api/payments/:id/status. Webhook aktualizuje status ASYNC (operator -> nasz serwer), więc
// pollujemy kilka razy. Teksty były stałe z czasów Przelewy24 i zawsze mówiły o zaliczce; od
// 12.10.2026 są neutralne, a „zaliczka” pada tylko przy zaliczce będącej CZĘŚCIĄ należności
// (`partial_advance` z serwera). Sam typ advance nie wystarczał: zaliczka równa całej kwocie (100%
// z widgetu) też ma ten typ, a Stripe nazywa ją „Opłata” (przegląd PR #212).

const Box = styled.div`
  max-width: 520px; margin-top: var(--sp-3);
  background: var(--card-bg); border: 1px solid var(--input-border);
  border-radius: var(--r-lg); box-shadow: var(--shadow-md); padding: var(--sp-6) 26px;
`;
const Big = styled.div`
  font-size: var(--fs-lg); font-weight: 700; margin-bottom: var(--sp-2); color: var(--text);
  display: flex; align-items: center; gap: var(--sp-2);
`;
const Dot = styled.span`
  width: 12px; height: 12px; border-radius: 50%;
  background: ${p => p.$c || 'var(--text-muted)'}; display: inline-block;
`;
const Muted = styled.p`color: var(--text-muted); margin: 0 0 var(--sp-5); font-size: var(--fs-sm);`;

// Tony jako referencje do tokenow (nie hex) — dzialaja w obu motywach.
const STATUS_TONE = {
  pending: 'var(--warning)',
  paid: 'var(--success)',
  failed: 'var(--danger)',
  cancelled: 'var(--danger)',
  unknown: 'var(--text-muted)',
};

function PaymentReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [partialAdvance, setPartialAdvance] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const timer = useRef(null);
  const tries = useRef(0);
  const paymentId = new URLSearchParams(window.location.search).get('p');

  useEffect(() => {
    if (!paymentId) { setStatus('unknown'); return undefined; }
    let alive = true;

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/payments/${paymentId}/status`, { credentials: 'include' });
        if (res.status === 404) { if (alive) setStatus('unknown'); return; }
        const data = await res.json();
        const s = data?.payment?.status || 'pending';
        if (!alive) return;
        setStatus(s);
        setPartialAdvance(data?.payment?.type === 'advance' && data?.payment?.partial_advance === true);
        // Kontynuuj polling gdy pending i są jeszcze próby; inaczej oznacz „utknięte".
        if (s === 'pending') {
          if (tries.current < 8) { tries.current += 1; timer.current = setTimeout(poll, 2000); }
          else setTimedOut(true);
        }
      } catch (e) {
        if (alive && tries.current < 8) { tries.current += 1; timer.current = setTimeout(poll, 2500); }
      }
    };
    poll();

    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [paymentId]);

  const knownStatus = STATUS_TONE[status] ? status : 'unknown';
  const tone = STATUS_TONE[knownStatus];
  const title = t(`paymentReturn.status.${knownStatus}.title`);
  const desc = knownStatus === 'paid' && partialAdvance
    ? t('paymentReturn.status.paid.descAdvance')
    : t(`paymentReturn.status.${knownStatus}.desc`);
  const stuck = status === 'pending' && timedOut;

  return (
    <PanelContainer>
      <Title>{t('paymentReturn.title')}</Title>
      <Subtitle>{t('paymentReturn.subtitle')}</Subtitle>
      <Box>
        <Big><Dot $c={tone} />{title}</Big>
        <Muted data-cy="payment-return-desc">{stuck ? t('paymentReturn.stuckDesc') : desc}</Muted>
        {stuck && (
          <PrimaryButton onClick={() => window.location.reload()} style={{ marginRight: 10 }}>{t('paymentReturn.retry')}</PrimaryButton>
        )}
        <PrimaryButton onClick={() => navigate('/calendar')}>{t('paymentReturn.backToCalendar')}</PrimaryButton>
      </Box>
    </PanelContainer>
  );
}

export default PaymentReturn;
